import React, { useRef, useEffect, useState, useCallback } from 'react';

const W = 600;
const H = 450;

// Base constants that will be scaled
const BIRD_X = 80;
const BIRD_W = 36;
const BIRD_H = 28;
const PIPE_W = 52;
const PIPE_GAP = 190;
const MIN_PIPE_GAP = 135;
const PIPE_INTERVAL = 2000;
const MIN_PIPE_INTERVAL = 1200;
const GAP_DECREASE_PER_SCORE = 4;
const INTERVAL_DECREASE_PER_SCORE = 60;
const GRAVITY = 0.10;
const JUMP_V = -3.6;
const PIPE_SPEED = 1.2;

const getPipeGap = (score) => Math.max(MIN_PIPE_GAP, PIPE_GAP - score * GAP_DECREASE_PER_SCORE);
const getPipeInterval = (score) => Math.max(MIN_PIPE_INTERVAL, PIPE_INTERVAL - score * INTERVAL_DECREASE_PER_SCORE);

function isMobile() {
  return typeof window !== 'undefined' && window.innerWidth < 768;
}

export default function FlappyBird() {
  const [mobileMult] = useState(isMobile() ? 4 : 1);
  const [mobileGrav] = useState(GRAVITY * (isMobile() ? 1.8 : 1));
  const [mobileJump] = useState(JUMP_V * (isMobile() ? 1.2 : 1));
  const canvasRef = useRef(null);
  const stateRef = useRef({
    phase: 'START', // START | PLAYING | DEAD
    birdY: H / 2,
    birdVY: 0,
    pipes: [],
    score: 0,
    bestScore: parseInt(localStorage.getItem('flappyBest') || '0'),
    lastPipeTime: 0,
    particles: [],
    deathFlash: 0,
    bgStars: Array.from({ length: 60 }, () => ({
      x: Math.random() * W,
      y: Math.random() * H,
      r: Math.random() * 1.5 + 0.5,
      alpha: Math.random() * 0.6 + 0.2,
      speed: Math.random() * 0.4 + 0.1,
    })),
    scanlineY: 0,
    time: 0,
    groundOffset: 0,
  });
  const [displayPhase, setDisplayPhase] = useState('START');
  const [displayScore, setDisplayScore] = useState(0);
  const [displayBest, setDisplayBest] = useState(parseInt(localStorage.getItem('flappyBest') || '0'));
  const rafRef = useRef(null);
  const lastTimeRef = useRef(null);

  // ── helpers ──────────────────────────────────────────────────────────────
  const spawnPipe = useCallback(() => {
    const gap = getPipeGap(stateRef.current.score);
    const gapTop = Math.random() * (H - gap - 120) + 60;
    stateRef.current.pipes.push({ x: W + 10, gapTop, gap, passed: false });
  }, []);

  const spawnParticles = useCallback((x, y, color) => {
    for (let i = 0; i < 18; i++) {
      const angle = Math.random() * Math.PI * 2;
      const speed = Math.random() * 5 + 2;
      stateRef.current.particles.push({
        x, y,
        vx: Math.cos(angle) * speed,
        vy: Math.sin(angle) * speed,
        life: 1,
        color,
      });
    }
  }, []);

  const reset = useCallback(() => {
    const s = stateRef.current;
    s.birdY = H / 2;
    s.birdVY = 0;
    s.pipes = [];
    s.score = 0;
    s.particles = [];
    s.deathFlash = 0;
    s.lastPipeTime = 0;
    s.groundOffset = 0;
    s.phase = 'PLAYING';
    setDisplayScore(0);
    setDisplayPhase('PLAYING');
  }, []);

  const doJump = useCallback(() => {
    const s = stateRef.current;
    if (s.phase === 'START') { reset(); return; }
    if (s.phase === 'PLAYING') {
      s.birdVY = mobileJump;
      spawnParticles(BIRD_X + BIRD_W / 2, s.birdY + BIRD_H / 2, '#00d9ff');
    }
    if (s.phase === 'DEAD') { reset(); }
  }, [reset, spawnParticles, mobileJump]);

  // ── draw ──────────────────────────────────────────────────────────────────
  const draw = useCallback((ctx, s, dt) => {
    const t = s.time;

    // ── background ──
    ctx.fillStyle = '#050818';
    ctx.fillRect(0, 0, W, H);

    // grid
    ctx.save();
    ctx.strokeStyle = 'rgba(0,217,255,0.06)';
    ctx.lineWidth = 1;
    const gridSize = 50;
    for (let x = 0; x < W; x += gridSize) { ctx.beginPath(); ctx.moveTo(x, 0); ctx.lineTo(x, H); ctx.stroke(); }
    for (let y = 0; y < H; y += gridSize) { ctx.beginPath(); ctx.moveTo(0, y); ctx.lineTo(W, y); ctx.stroke(); }
    ctx.restore();

    // moving stars
    s.bgStars.forEach(star => {
      star.x -= star.speed * (s.phase === 'PLAYING' ? 1.5 : 0.5);
      if (star.x < 0) { star.x = W; star.y = Math.random() * H; }
      ctx.save();
      ctx.globalAlpha = star.alpha * (0.7 + 0.3 * Math.sin(t * 0.02 + star.x));
      ctx.fillStyle = '#ffffff';
      ctx.shadowColor = '#00d9ff';
      ctx.shadowBlur = 4;
      ctx.beginPath(); ctx.arc(star.x, star.y, star.r, 0, Math.PI * 2); ctx.fill();
      ctx.restore();
    });

    // ambient glow
    const grd = ctx.createRadialGradient(W * 0.5, H * 0.4, 0, W * 0.5, H * 0.4, 250);
    grd.addColorStop(0, 'rgba(0,217,255,0.04)');
    grd.addColorStop(1, 'transparent');
    ctx.fillStyle = grd;
    ctx.fillRect(0, 0, W, H);

    // ── ground ──
    const groundH = 28;
    const groundY = H - groundH;
    if (s.phase === 'PLAYING') s.groundOffset = (s.groundOffset + PIPE_SPEED * mobileMult) % 50;
    // grid tiles on ground
    ctx.save();
    ctx.fillStyle = '#0a0e27';
    ctx.fillRect(0, groundY, W, groundH);
    // animated lines
    ctx.strokeStyle = 'rgba(0,217,255,0.25)';
    ctx.lineWidth = 1;
    for (let x = -50 + (s.groundOffset % 50); x < W; x += 50) {
      ctx.beginPath(); ctx.moveTo(x, groundY); ctx.lineTo(x, H); ctx.stroke();
    }
    ctx.strokeStyle = 'rgba(0,217,255,0.12)';
    ctx.lineWidth = 1;
    for (let y = groundY + 8; y < H; y += 8) {
      ctx.beginPath(); ctx.moveTo(0, y); ctx.lineTo(W, y); ctx.stroke();
    }
    // top border neon line
    const lineGrd = ctx.createLinearGradient(0, 0, W, 0);
    lineGrd.addColorStop(0, '#ff006e');
    lineGrd.addColorStop(0.5, '#00d9ff');
    lineGrd.addColorStop(1, '#d000ff');
    ctx.shadowColor = '#00d9ff';
    ctx.shadowBlur = 10;
    ctx.strokeStyle = lineGrd;
    ctx.lineWidth = 2;
    ctx.beginPath(); ctx.moveTo(0, groundY); ctx.lineTo(W, groundY); ctx.stroke();
    ctx.restore();

    // ── pipes ──
    s.pipes.forEach(pipe => {
      const topH = pipe.gapTop;
      const botY = pipe.gapTop + (pipe.gap ?? PIPE_GAP);
      const botH = groundY - botY;

      [
        { y: 0, h: topH, capY: topH - 18, capDir: 'bottom' },
        { y: botY, h: botH, capY: botY, capDir: 'top' },
      ].forEach(({ y, h, capY, capDir }) => {
        if (h <= 0) return;

        // pipe body
        const pipeGrd = ctx.createLinearGradient(pipe.x, 0, pipe.x + PIPE_W, 0);
        pipeGrd.addColorStop(0, 'rgba(208,0,255,0.15)');
        pipeGrd.addColorStop(0.3, 'rgba(208,0,255,0.6)');
        pipeGrd.addColorStop(0.7, 'rgba(208,0,255,0.4)');
        pipeGrd.addColorStop(1, 'rgba(208,0,255,0.1)');

        ctx.save();
        ctx.shadowColor = '#d000ff';
        ctx.shadowBlur = 16;
        ctx.fillStyle = pipeGrd;
        ctx.fillRect(pipe.x, y, PIPE_W, h);

        // edge lines
        ctx.strokeStyle = '#d000ff';
        ctx.lineWidth = 2;
        ctx.strokeRect(pipe.x, y, PIPE_W, h);

        // inner glow strip
        ctx.fillStyle = 'rgba(208,0,255,0.15)';
        ctx.fillRect(pipe.x + 8, y, PIPE_W - 16, h);

        // cap
        const capH = 18;
        const capX = pipe.x - 5;
        const capW = PIPE_W + 10;
        const cY = capDir === 'bottom' ? capY : capY - capH;

        const capGrd = ctx.createLinearGradient(capX, 0, capX + capW, 0);
        capGrd.addColorStop(0, '#5500aa');
        capGrd.addColorStop(0.3, '#d000ff');
        capGrd.addColorStop(0.7, '#aa00cc');
        capGrd.addColorStop(1, '#440088');
        ctx.fillStyle = capGrd;
        ctx.shadowBlur = 20;
        ctx.fillRect(capX, cY, capW, capH);
        ctx.strokeStyle = '#e040fb';
        ctx.lineWidth = 2;
        ctx.strokeRect(capX, cY, capW, capH);

        ctx.restore();
      });
    });

    // ── bird ──
    const bx = BIRD_X;
    const by = s.birdY;
    const angle = Math.max(-0.5, Math.min(0.6, s.birdVY * 0.06));
    const wingFlap = Math.sin(t * 0.25) * 0.4;

    ctx.save();
    ctx.translate(bx + BIRD_W / 2, by + BIRD_H / 2);
    ctx.rotate(angle);

    // glow halo
    const haloR = 28 + Math.sin(t * 0.05) * 4;
    const halo = ctx.createRadialGradient(0, 0, 4, 0, 0, haloR);
    halo.addColorStop(0, 'rgba(0,217,255,0.25)');
    halo.addColorStop(1, 'transparent');
    ctx.fillStyle = halo;
    ctx.beginPath(); ctx.arc(0, 0, haloR, 0, Math.PI * 2); ctx.fill();

    // wing (behind)
    ctx.save();
    ctx.rotate(wingFlap);
    ctx.shadowColor = '#00d9ff';
    ctx.shadowBlur = 12;
    ctx.fillStyle = '#0099ff';
    ctx.beginPath();
    ctx.ellipse(-4, 2, 14, 7, -0.3, 0, Math.PI * 2);
    ctx.fill();
    ctx.strokeStyle = '#00d9ff';
    ctx.lineWidth = 1.5;
    ctx.stroke();
    ctx.restore();

    // body
    ctx.shadowColor = '#00d9ff';
    ctx.shadowBlur = 18;
    ctx.fillStyle = '#0a1a3a';
    ctx.strokeStyle = '#00d9ff';
    ctx.lineWidth = 2;
    ctx.beginPath();
    ctx.roundRect(-BIRD_W / 2 + 2, -BIRD_H / 2 + 2, BIRD_W - 4, BIRD_H - 4, 8);
    ctx.fill();
    ctx.stroke();

    // inner body detail lines
    ctx.shadowBlur = 0;
    ctx.strokeStyle = 'rgba(0,217,255,0.3)';
    ctx.lineWidth = 1;
    ctx.beginPath(); ctx.moveTo(-6, -BIRD_H / 2 + 6); ctx.lineTo(-6, BIRD_H / 2 - 6); ctx.stroke();
    ctx.beginPath(); ctx.moveTo(2, -BIRD_H / 2 + 6); ctx.lineTo(2, BIRD_H / 2 - 6); ctx.stroke();

    // cockpit / eye
    ctx.shadowColor = '#ff006e';
    ctx.shadowBlur = 15;
    ctx.fillStyle = '#ff006e';
    ctx.beginPath(); ctx.arc(10, -2, 6, 0, Math.PI * 2); ctx.fill();
    ctx.fillStyle = '#fff';
    ctx.shadowBlur = 0;
    ctx.beginPath(); ctx.arc(11, -3, 2, 0, Math.PI * 2); ctx.fill();

    // thruster jet
    const jetLen = 8 + Math.random() * 6;
    const jGrd = ctx.createLinearGradient(-BIRD_W / 2 - jetLen, 0, -BIRD_W / 2, 0);
    jGrd.addColorStop(0, 'transparent');
    jGrd.addColorStop(1, '#00ffff');
    ctx.shadowColor = '#00d9ff';
    ctx.shadowBlur = 12;
    ctx.fillStyle = jGrd;
    ctx.beginPath();
    ctx.moveTo(-BIRD_W / 2, -4);
    ctx.lineTo(-BIRD_W / 2 - jetLen, 0);
    ctx.lineTo(-BIRD_W / 2, 4);
    ctx.closePath();
    ctx.fill();

    ctx.restore();

    // ── particles ──
    s.particles = s.particles.filter(p => {
      ctx.save();
      ctx.globalAlpha = p.life;
      ctx.shadowColor = p.color;
      ctx.shadowBlur = 8;
      ctx.fillStyle = p.color;
      ctx.beginPath(); ctx.arc(p.x, p.y, 3 * p.life, 0, Math.PI * 2); ctx.fill();
      ctx.restore();
      p.x += p.vx; p.y += p.vy;
      p.vy += 0.15;
      p.life -= 0.04;
      return p.life > 0;
    });

    // ── score ──
    ctx.save();
    ctx.font = 'bold 36px "VT323", monospace';
    ctx.textAlign = 'center';
    ctx.shadowColor = '#00d9ff';
    ctx.shadowBlur = 20;
    ctx.fillStyle = '#00d9ff';
    ctx.fillText(s.score, W / 2, 50);
    ctx.restore();

    // ── scanline ──
    s.scanlineY = (s.scanlineY + 1.5) % H;
    ctx.save();
    ctx.globalAlpha = 0.06;
    const slGrd = ctx.createLinearGradient(0, s.scanlineY - 10, 0, s.scanlineY + 10);
    slGrd.addColorStop(0, 'transparent');
    slGrd.addColorStop(0.5, '#00d9ff');
    slGrd.addColorStop(1, 'transparent');
    ctx.fillStyle = slGrd;
    ctx.fillRect(0, s.scanlineY - 10, W, 20);
    ctx.restore();

    // CRT vignette
    const vgrd = ctx.createRadialGradient(W / 2, H / 2, H * 0.3, W / 2, H / 2, H * 0.9);
    vgrd.addColorStop(0, 'transparent');
    vgrd.addColorStop(1, 'rgba(0,0,0,0.45)');
    ctx.fillStyle = vgrd;
    ctx.fillRect(0, 0, W, H);

    // death flash
    if (s.deathFlash > 0) {
      ctx.save();
      ctx.globalAlpha = s.deathFlash * 0.5;
      ctx.fillStyle = '#ff006e';
      ctx.fillRect(0, 0, W, H);
      ctx.restore();
      s.deathFlash = Math.max(0, s.deathFlash - 0.06);
    }
  }, [mobileMult]);

  // ── update ────────────────────────────────────────────────────────────────
  const update = useCallback((dt, nowMs) => {
    const s = stateRef.current;
    if (s.phase !== 'PLAYING') return;

    s.time += 1;

    // physics
    s.birdVY += mobileGrav;
    s.birdY += s.birdVY;

    // ceiling
    if (s.birdY < 0) { s.birdY = 0; s.birdVY = 2; }

    // hit ground
    if (s.birdY + BIRD_H >= H - 28) {
      s.birdY = H - 28 - BIRD_H;
      s.phase = 'DEAD';
      s.deathFlash = 1;
      spawnParticles(BIRD_X + BIRD_W / 2, s.birdY + BIRD_H / 2, '#ff006e');
      if (s.score > s.bestScore) {
        s.bestScore = s.score;
        localStorage.setItem('flappyBest', s.score);
        setDisplayBest(s.score);
      }
      setDisplayPhase('DEAD');
      return;
    }

    // spawn pipes
    if (nowMs - s.lastPipeTime > getPipeInterval(s.score)) {
      spawnPipe();
      s.lastPipeTime = nowMs;
    }

    // move & score pipes
    s.pipes = s.pipes.filter(p => p.x + PIPE_W > -10);
    s.pipes.forEach(pipe => {
      pipe.x -= PIPE_SPEED * mobileMult;
      if (!pipe.passed && pipe.x + PIPE_W < BIRD_X) {
        pipe.passed = true;
        s.score += 1;
        setDisplayScore(s.score);
        spawnParticles(BIRD_X, s.birdY, '#d000ff');
      }

      // collision
      const bL = BIRD_X + 4, bR = BIRD_X + BIRD_W - 4;
      const bT = s.birdY + 4, bB = s.birdY + BIRD_H - 4;
      const pL = pipe.x, pR = pipe.x + PIPE_W;
      const inX = bR > pL && bL < pR;
      if (inX && (bT < pipe.gapTop || bB > pipe.gapTop + (pipe.gap ?? PIPE_GAP))) {
        s.phase = 'DEAD';
        s.deathFlash = 1;
        spawnParticles(BIRD_X + BIRD_W / 2, s.birdY + BIRD_H / 2, '#ff006e');
        if (s.score > s.bestScore) {
          s.bestScore = s.score;
          localStorage.setItem('flappyBest', s.score);
          setDisplayBest(s.score);
        }
        setDisplayPhase('DEAD');
      }
    });
  }, [spawnPipe, spawnParticles, mobileMult, mobileGrav]);

  // ── loop ──────────────────────────────────────────────────────────────────
  useEffect(() => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    let running = true;

    const loop = (now) => {
      if (!running) return;
      const dt = lastTimeRef.current ? now - lastTimeRef.current : 16;
      lastTimeRef.current = now;
      stateRef.current.time += 1;
      update(dt, now);
      draw(ctx, stateRef.current, dt);
      rafRef.current = requestAnimationFrame(loop);
    };

    rafRef.current = requestAnimationFrame(loop);
    return () => { running = false; cancelAnimationFrame(rafRef.current); };
  }, [update, draw]);

  // ── input ─────────────────────────────────────────────────────────────────
  useEffect(() => {
    const onKey = (e) => { if (e.code === 'Space' || e.code === 'ArrowUp' || e.code === 'KeyW') { e.preventDefault(); doJump(); } };
    const canvas = canvasRef.current;
    const onTouch = (e) => { e.preventDefault(); doJump(); };
    const onClick = (e) => { e.preventDefault(); doJump(); };
    window.addEventListener('keydown', onKey);
    canvas?.addEventListener('touchstart', onTouch, { passive: false });
    canvas?.addEventListener('mousedown', onClick);
    return () => {
      window.removeEventListener('keydown', onKey);
      canvas?.removeEventListener('touchstart', onTouch);
      canvas?.removeEventListener('mousedown', onClick);
    };
  }, [doJump]);

  return (
    <div className="flex flex-col items-center" style={{ maxWidth: '520px', margin: '0 auto', width: '100%', padding: '0.5rem' }}>
      <div className="flex justify-between w-full mb-3" style={{ gap: '1rem' }}>
        <div style={{ color: '#00d9ff', fontFamily: 'monospace', fontSize: 'clamp(14px, 4vw, 24px)', fontWeight: 'bold', textShadow: '0 0 10px #00d9ff' }}>
          Score: {displayScore}
        </div>
        <div style={{ color: '#ff006e', fontFamily: 'monospace', fontSize: 'clamp(12px, 3.5vw, 18px)', textShadow: '0 0 10px #ff006e' }}>
          Best: {displayBest}
        </div>
      </div>

      <div
        style={{
          position: 'relative',
          width: '100%',
          maxWidth: '520px',
          aspectRatio: `${W}/${H}`,
          borderRadius: 8,
          overflow: 'hidden',
          border: '2px solid rgba(0,217,255,0.3)',
          boxShadow: '0 0 20px rgba(0,217,255,0.2)',
          touchAction: 'none',
        }}
      >
        <canvas
          ref={canvasRef}
          width={W}
          height={H}
          style={{
            width: '100%',
            height: '100%',
            display: 'block',
            cursor: 'pointer',
            touchAction: 'none',
          }}
        />

        {displayPhase === 'START' && (
          <div
            className="absolute inset-0 flex flex-col items-center justify-center"
            style={{
              background: 'rgba(10,14,39,0.9)',
              border: '1px solid #00d9ff',
              boxShadow: '0 0 30px rgba(0,217,255,0.3)',
              touchAction: 'none',
            }}
            onTouchStart={(e) => { e.preventDefault(); doJump(); }}
            onClick={doJump}
          >
            <p style={{ color: '#00d9ff', fontFamily: 'monospace', fontSize: '20px', textShadow: '0 0 15px #00d9ff', marginBottom: 8 }}>
              Tap or Click to Start
            </p>
          </div>
        )}

        {displayPhase === 'DEAD' && (
          <div
            className="absolute inset-0 flex flex-col items-center justify-center"
            style={{
              background: 'rgba(10,14,39,0.9)',
              border: '1px solid #00d9ff',
              boxShadow: '0 0 30px rgba(0,217,255,0.3)',
              touchAction: 'none',
            }}
          >
            <p style={{ color: '#ff006e', fontFamily: 'monospace', fontSize: 'clamp(20px, 6vw, 28px)', fontWeight: 'bold', textShadow: '0 0 20px #ff006e', marginBottom: '10px' }}>
              GAME OVER
            </p>
            <p style={{ color: '#00d9ff', fontFamily: 'monospace', fontSize: 'clamp(14px, 4vw, 18px)', textShadow: '0 0 10px #00d9ff', marginBottom: '5px' }}>
              Score: {displayScore}
            </p>
            <p style={{ color: '#ff006e', fontFamily: 'monospace', fontSize: 'clamp(12px, 3.5vw, 16px)', textShadow: '0 0 10px #ff006e', marginBottom: '20px' }}>
              Best: {displayBest}
            </p>
            <button
              onTouchStart={(e) => { e.stopPropagation(); doJump(); }}
              onClick={doJump}
              style={{
                background: 'transparent',
                border: '2px solid #00d9ff',
                color: '#00d9ff',
                padding: '10px 24px',
                borderRadius: 8,
                fontFamily: 'monospace',
                cursor: 'pointer',
                boxShadow: '0 0 15px rgba(0,217,255,0.4)',
              }}
            >
              Play Again
            </button>
          </div>
        )}
      </div>
    </div>
  );
}